import { useEffect, useState } from "react";
import {
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid";

export default function Alert({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="rounded-md bg-yellow-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon
              className="h-5 w-5 text-yellow-400"
              aria-hidden="true"
            />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">{title}</h3>
            <div className="mt-2 text-sm text-yellow-700">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AlertError({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative rounded-lg border border-red-200 bg-gradient-to-r from-red-50 to-red-25 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
            <XCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-red-900">{title}</h3>
          <div className="mt-1 text-sm text-red-700 leading-relaxed">{children}</div>
        </div>
      </div>
      <div className="absolute inset-x-0 top-0 h-1 rounded-t-lg bg-gradient-to-r from-red-400 to-red-500"></div>
    </div>
  );
}

function useCountdown(targetTime: string | null) {
  const [timeLeft, setTimeLeft] = useState<{
    total: number;
    minutes: number;
    seconds: number;
  }>({ total: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!targetTime) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const target = new Date(targetTime).getTime();
      const difference = target - now;

      if (difference > 0) {
        const total = Math.floor(difference / 1000);
        const minutes = Math.floor(total / 60);
        const seconds = total % 60;
        setTimeLeft({ total, minutes, seconds });
      } else {
        setTimeLeft({ total: 0, minutes: 0, seconds: 0 });
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, [targetTime]);

  return timeLeft;
}

export function AlertRateLimit({ retryAfter }: { retryAfter?: string }) {
  const countdown = useCountdown(retryAfter || null);
  
  const formatTime = (minutes: number, seconds: number) => {
    if (minutes > 0) {
      const minuteLabel = minutes === 1 ? 'minute' : 'minutes';
      return `${minutes} ${minuteLabel} ${seconds} seconds`;
    }
    return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
  };

  return (
    <div className="ml-2 p-3 flex flex-row items-center gap-3 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex-shrink-0">
        <div className="relative inline-flex">
          <div className="w-2.5 h-2.5 bg-amber-500 rounded-full"></div>
          <div className="w-2.5 h-2.5 bg-amber-500 rounded-full absolute top-0 left-0 animate-pulse"></div>
        </div>
      </div>
      <div className="flex-1">
        <div className="text-sm text-slate-700">
          {countdown.total > 0 ? (
            <>
              Usage cap reached. Please wait <span className="font-medium text-slate-900">{formatTime(countdown.minutes, countdown.seconds)}</span> before sending.
            </>
          ) : (
            <span className="text-green-700">Ready to send messages again.</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Legacy version (keeping for backwards compatibility)
export function AlertRateLimitCard({ retryAfter }: { retryAfter?: string }) {
  const countdown = useCountdown(retryAfter || null);
  
  const formatTime = (minutes: number, seconds: number) => {
    if (minutes > 0) {
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
    }
    return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
  };

  return (
    <div className="relative rounded-lg border border-orange-200 bg-gradient-to-r from-orange-50 to-orange-25 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100">
            <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" aria-hidden="true" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-orange-900">
            You've reached the current usage cap
          </h3>
          <div className="mt-1 text-sm text-orange-700 leading-relaxed">
            {countdown.total > 0 ? (
              <>Please wait <strong>{formatTime(countdown.minutes, countdown.seconds)}</strong> before resending.</>
            ) : (
              'You can try sending your message again now.'
            )}
          </div>
        </div>
      </div>
      <div className="absolute inset-x-0 top-0 h-1 rounded-t-lg bg-gradient-to-r from-orange-400 to-orange-500"></div>
    </div>
  );
}
